import React from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { EntitlementBaseProduct } from '@atlassian/cssxp-types-entitlement';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { NewBillDate, getFormattedDateString } from '@css-xp/base';
import {
  isActiveEntitlement,
  isTrialEntitlement,
  getFormattedDate,
  validateFreeSubscriptions,
} from '@css-xp/entitlements-v2';
import { NoData } from './no-data';
import { ChangeBillDateButton } from './change-bill-date-button';

const getNextBillDate = (
  entitlementOrProduct: any,
  refetch?: () => Promise<ApolloQueryResult<unknown>>,
  shouldReturnString?: boolean,
  hideChangeBillDateButton = false
) => {
  const emptyValue = shouldReturnString ? '-' : <NoData />;
  const isActive = isActiveEntitlement(entitlementOrProduct);

  if (!isActive) {
    return emptyValue;
  }

  const isTrial = isTrialEntitlement(
    entitlementOrProduct,
    entitlementOrProduct.baseProduct === EntitlementBaseProduct.STATUS_PAGE
  );

  const lastTrialEndTimestamp = entitlementOrProduct.subscriptionTimestamps?.lastTrialEndTimestamp;
  if (isTrial && lastTrialEndTimestamp) {
    return shouldReturnString ? (
      getFormattedDateString({
        date: lastTrialEndTimestamp,
        format: NEW_DATE_FORMAT,
      })
    ) : (
      <>
        <div className="next-bill-date-section-ppc">{getFormattedDate(lastTrialEndTimestamp)}</div>
        <NewBillDate entitlement={entitlementOrProduct} isTrial={true} />
      </>
    );
  }

  const { latestPeriodEndDate } = entitlementOrProduct;
  const isFree = validateFreeSubscriptions(entitlementOrProduct);

  if (latestPeriodEndDate && !isFree) {
    return shouldReturnString ? (
      getFormattedDateString({ date: latestPeriodEndDate, format: NEW_DATE_FORMAT })
    ) : (
      <>
        <div className="next-bill-date-section-ppc">
          {getFormattedDate(latestPeriodEndDate)}
          {!hideChangeBillDateButton && (
            <ChangeBillDateButton entitlement={entitlementOrProduct} refetch={refetch as any} />
          )}
        </div>
        <NewBillDate entitlement={entitlementOrProduct} />
      </>
    );
  }

  return emptyValue;
};

// Used in Downloaded CSV for showing the next bill date
export const getNextBillDateText = (product: generatedTypesNew.TransactionAccountProduct | null) => {
  return getNextBillDate(product, undefined, true) as string;
};

// Used in Entitlement page for showing the next bill date
export const NextBillDate = ({
  entitlement,
  refetch,
  hideChangeBillDateButton = false,
}: {
  entitlement: generatedTypesNew.EntitlementDetails;
  hideChangeBillDateButton?: boolean;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
}) => {
  return getNextBillDate(entitlement, refetch, false, hideChangeBillDateButton) as React.ReactElement;
};
